import type {CapabilityContext} from './types/core'

/**
 * Runtime marker distinguishing a client-only capability from an SSR-safe one.
 * Brand authors wrap a resolver with `clientOnly(...)`; `resolveCapabilities`
 * inspects this marker and skips the resolver at SSR time (the key is omitted
 * from the resolved snapshot that crosses the JSON boundary). The hook path
 * `useBrandCapabilities` resolves the marker normally, using the live client
 * context.
 *
 * Why a symbol: non-enumerable on serialized output and impossible to forge
 * accidentally (can't collide with a brand config field name).
 */
const CLIENT_ONLY = Symbol.for('@vio/core/brand/capabilities/clientOnly')

export type ClientOnly<T, Ctx = CapabilityContext> = {
  [CLIENT_ONLY]: true
  resolve: (ctx: Ctx) => T
}

/**
 * Wrap a resolver to mark it client-only. Use for capabilities whose resolver
 * needs inputs not available at SSR time (`locale`, `isAuthenticated`,
 * experiment variants, etc.). The SSR prop snapshot will not include this
 * key — consumers must read the value via `useBrandCapabilities`.
 */
export const clientOnly = <T, Ctx = CapabilityContext>(
  resolve: (ctx: Ctx) => T
): ClientOnly<T, Ctx> => ({
  [CLIENT_ONLY]: true,
  resolve
})

/**
 * Type guard for the `ClientOnly` marker. Used by `resolveCapabilities` to
 * branch at runtime.
 */
export const isClientOnly = <T>(
  value: unknown
): value is ClientOnly<T, unknown> => {
  if (typeof value !== 'object' || value === null) return false
  return (value as Record<symbol, unknown>)[CLIENT_ONLY] === true
}
